import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronRight, Download, Loader2, Plus, RefreshCw, Search, ShieldAlert, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { INCIDENT_SEVERITY_LABELS, INCIDENT_STATE_FILTERS, RELEASE_PRODUCT_LABELS, formatDate, formatDuration, productErrorMessage } from "../lib/product";
import { IncidentStateBadge, SeverityBadge } from "../components/product/ProductBits";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ALL = "__all__";
const PAGE_SIZE = 50;
const STATE_IDS = INCIDENT_STATE_FILTERS.map((s) => s.id).filter(Boolean);

function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters = {
    state: STATE_IDS.includes(params.get("state")) ? params.get("state") : "",
    severity: Object.keys(INCIDENT_SEVERITY_LABELS).includes(params.get("severity")) ? params.get("severity") : "",
    product: Object.keys(RELEASE_PRODUCT_LABELS).includes(params.get("product")) ? params.get("product") : "",
    search: params.get("q") || "",
  };
  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        const name = key === "search" ? "q" : key;
        if (value === "" || value == null) next.delete(name);
        else next.set(name, value);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [filters, update];
}

function MobileRow({ incident }) {
  return (
    <Link to={`/produit/incidents/${incident.id}`} className="flex items-start gap-3 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`incident-card-${incident.number}`}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="text-xs text-slate-500 tabular-nums">{incident.number} · {RELEASE_PRODUCT_LABELS[incident.product]}</div>
        <div className="font-medium text-slate-900 break-words">{incident.title}</div>
        <div className="flex flex-wrap gap-1.5"><SeverityBadge severity={incident.severity} /><IncidentStateBadge state={incident.state} /></div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3">
          <span>Depuis le {formatDate(incident.started_at)}</span>
          <span>{formatDuration(incident.duration_minutes)}</span>
          {incident.tickets_count > 0 && <span>{incident.tickets_count} ticket{incident.tickets_count > 1 ? "s" : ""}</span>}
        </div>
        {incident.postmortem_due && <p className="text-xs font-medium text-red-700">Post-mortem en retard</p>}
      </div>
      <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
    </Link>
  );
}

// Incidents de production (D-40): what hurt the service, how long, how many tickets — a pharmacy is counted, never named.
export default function ProductIncidents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, update] = useFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [data, setData] = useState({ incidents: [], total: 0 });
  const [phase, setPhase] = useState("loading");
  const [errorText, setErrorText] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const seq = useRef(0);
  const canManage = hasPermission(user, "product:manage_incidents");
  const canExport = hasPermission(user, "product:manage_roadmap");

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== filters.search) update({ search: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const out = { limit: PAGE_SIZE };
    for (const key of ["state", "severity", "product", "search"]) if (filters[key]) out[key] = filters[key];
    return out;
  }, [filters.state, filters.severity, filters.product, filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get("/product/incidents", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setErrorText(productErrorMessage(e, "Impossible de charger les incidents."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const loadMore = async () => {
    if (loadingMore) return;
    const mine = seq.current;
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/product/incidents", { params: { ...query, skip: data.incidents.length } });
      if (mine !== seq.current) return;
      setData((prev) => ({ ...body, incidents: [...prev.incidents, ...body.incidents.filter((r) => !prev.incidents.some((p) => p.id === r.id))] }));
    } catch (e) {
      if (mine === seq.current) toast.error(productErrorMessage(e, "Impossible de charger la suite."));
    } finally {
      if (mine === seq.current) setLoadingMore(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data: blob } = await api.get("/product/export/incidents.csv", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "produit-incidents.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch (e) {
      toast.error(productErrorMessage(e, "Export impossible. Réessayez dans quelques instants."));
    } finally {
      setExporting(false);
    }
  };

  const filtered = !!filters.state || !!filters.severity || !!filters.product || !!filters.search;
  const reset = () => { setSearchInput(""); update({ state: "", severity: "", product: "", search: "" }); };

  return (
    <div className="space-y-4" data-testid="incidents-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Incidents de production</h1>
          <p className="text-sm text-slate-500">Ce qui a perturbé le service, comment on l'a traité et ce qu'on en retient.</p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="incidents-export">
              {exporting ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Download size={16} className="mr-1.5" />}Exporter
            </Button>
          )}
          {canManage && <Button onClick={() => navigate("/produit/incidents/nouveau")} data-testid="incident-new"><Plus size={16} className="mr-1.5" />Nouvel incident</Button>}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto" role="tablist" aria-label="État des incidents" data-testid="incidents-states">
        {INCIDENT_STATE_FILTERS.map((s) => (
          <button key={s.id || "all"} type="button" role="tab" aria-selected={filters.state === s.id} onClick={() => update({ state: s.id })} data-testid={`incidents-state-${s.id || "all"}`}
            className={`flex-auto sm:flex-none rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${filters.state === s.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro ou titre…" className="pl-9 h-9" aria-label="Rechercher un incident" data-testid="incidents-search" />
        </div>
        <Select value={filters.severity || ALL} onValueChange={(v) => update({ severity: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-[calc(50%-0.25rem)] sm:w-44" aria-label="Gravité" data-testid="incidents-severity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les gravités</SelectItem>
            {Object.entries(INCIDENT_SEVERITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.product || ALL} onValueChange={(v) => update({ product: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-[calc(50%-0.25rem)] sm:w-52" aria-label="Logiciel" data-testid="incidents-product-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les logiciels</SelectItem>
            {Object.entries(RELEASE_PRODUCT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset} data-testid="incidents-reset"><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2" data-testid="incidents-loading"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="incidents-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{errorText}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}

      {phase === "ready" && data.incidents.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="incidents-empty">
          <ShieldAlert className="mx-auto text-slate-300" size={26} />
          {filtered ? (
            <>
              <h3 className="font-medium text-slate-900">Aucun incident ne correspond</h3>
              <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button>
            </>
          ) : (
            <>
              <h3 className="font-medium text-slate-900">Aucun incident enregistré</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Quand le service est perturbé, ouvrez un incident : sa chronologie, ses tickets et son post-mortem restent au même endroit.</p>
              {canManage && <Button size="sm" onClick={() => navigate("/produit/incidents/nouveau")}><Plus size={14} className="mr-1.5" />Nouvel incident</Button>}
            </>
          )}
        </Card>
      )}

      {phase === "ready" && data.incidents.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="incidents-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Incident</TableHead><TableHead className="hidden xl:table-cell">Logiciel</TableHead><TableHead>Gravité</TableHead><TableHead>État</TableHead>
                  <TableHead>Début</TableHead><TableHead>Durée</TableHead><TableHead className="hidden xl:table-cell">Tickets</TableHead><TableHead className="hidden xl:table-cell">Responsable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.incidents.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate(`/produit/incidents/${r.id}`)} data-testid={`incident-row-${r.number}`}>
                    <TableCell>
                      <Link to={`/produit/incidents/${r.id}`} className="font-medium text-slate-900 hover:underline break-words" onClick={(e) => e.stopPropagation()}>{r.title}</Link>
                      <div className="text-xs text-slate-500 tabular-nums">{r.number}<span className="xl:hidden"> · {RELEASE_PRODUCT_LABELS[r.product]}</span>{r.postmortem_due && <span className="ml-2 font-medium text-red-700">Post-mortem en retard</span>}</div>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-sm">{RELEASE_PRODUCT_LABELS[r.product]}</TableCell>
                    <TableCell><SeverityBadge severity={r.severity} /></TableCell>
                    <TableCell><IncidentStateBadge state={r.state} /></TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDate(r.started_at)}</TableCell>
                    <TableCell className="text-sm tabular-nums whitespace-nowrap">{formatDuration(r.duration_minutes)}</TableCell>
                    <TableCell className="hidden xl:table-cell text-sm tabular-nums">{r.tickets_count || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="hidden xl:table-cell text-sm">{r.responsible_name || <span className="text-slate-400">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden overflow-hidden" data-testid="incidents-list-mobile">{data.incidents.map((r) => <MobileRow key={r.id} incident={r} />)}</Card>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="incidents-count">{data.incidents.length} sur {data.total} incident{data.total > 1 ? "s" : ""}</span>
            {data.incidents.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="incidents-load-more">
                {loadingMore ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}Charger plus
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
